/**
 * BIM drawing alignment inference (convergent snapping).
 *
 * While a BIM element is being drawn on the ground plane, the cursor is
 * tested against the endpoints of the existing walls: whenever it comes
 * within tolerance of a ground-parallel alignment line through an endpoint
 * (X-aligned or Y-aligned), it snaps onto that line and the alignment is
 * previewed as a dashed guide — the plan-drawing analogue of the 3D
 * top-handle elevation guides (WallShapeHandleLayer).
 *
 * Two independent axis matches converge: an X match through one endpoint and
 * a Y match through another resolve to their corner intersection, which is
 * the classic BIM "weak reference" alignment behavior.
 */

import * as THREE from 'three';

/** One endpoint of the existing model usable as an alignment reference. */
export interface AlignmentReference {
    x: number;
    y: number;
    /** Base elevation of the source element (guides draw slightly above it). */
    z: number;
}

/** A dashed guide segment previewing one active alignment. */
export interface AlignmentGuide {
    from: [number, number, number];
    to: [number, number, number];
}

export interface AlignmentResult {
    /** The cursor point after convergent alignment snapping. */
    point: THREE.Vector3;
    guides: AlignmentGuide[];
}

interface WallEntityLike {
    type?: string;
    data?: { start?: number[]; end?: number[] };
}

/** Tolerance above which a span is padded with a fixed margin. */
const GUIDE_MARGIN = 0.5;

/**
 * Extract alignment references (start/end points) from the placed walls.
 * Plain numbers only — the result is recomputed when the entity map changes,
 * not per pointer event.
 */
export function collectWallAlignmentReferences(entities: Record<string, WallEntityLike>): AlignmentReference[] {
    const refs: AlignmentReference[] = [];
    for (const entity of Object.values(entities ?? {})) {
        if (!entity || entity.type !== 'wall') continue;
        const d = entity.data;
        if (Array.isArray(d?.start)) refs.push({ x: d.start[0], y: d.start[1], z: d.start[2] ?? 0 });
        if (Array.isArray(d?.end)) refs.push({ x: d.end[0], y: d.end[1], z: d.end[2] ?? 0 });
    }
    return refs;
}

/**
 * Resolve the snapped draft point against the alignment references.
 *
 * For each axis, the nearest reference within `tolerance` wins; the cursor is
 * snapped onto its alignment line. Both axes may snap at once (convergence to
 * a corner). Guides cover every reference sharing the engaged line, drawn
 * from below the lowest to above the highest participant (cursor included).
 */
export function resolveAlignment(
    cursor: THREE.Vector3,
    references: AlignmentReference[],
    tolerance: number,
    /** The in-progress wall's own anchor counts as a reference too. */
    extra?: AlignmentReference | null
): AlignmentResult {
    const refs = extra ? [...references, extra] : references;
    const point = cursor.clone();

    let bestX: AlignmentReference | null = null;
    let bestXDev = tolerance;
    let bestY: AlignmentReference | null = null;
    let bestYDev = tolerance;
    for (const ref of refs) {
        const dx = Math.abs(ref.x - cursor.x);
        if (dx <= bestXDev) {
            bestXDev = dx;
            bestX = ref;
        }
        const dy = Math.abs(ref.y - cursor.y);
        if (dy <= bestYDev) {
            bestYDev = dy;
            bestY = ref;
        }
    }

    const guides: AlignmentGuide[] = [];
    if (bestX) {
        point.x = bestX.x;
        guides.push(...axisGuides(refs, 'x', bestX.x, cursor.y, point.y));
    }
    if (bestY) {
        point.y = bestY.y;
        guides.push(...axisGuides(refs, 'y', bestY.y, cursor.x, point.x));
    }
    return { point, guides };
}

/** Vertical (axis 'x') or horizontal (axis 'y') guide segments for every
 *  reference lying on the engaged alignment line. */
function axisGuides(
    refs: AlignmentReference[],
    axis: 'x' | 'y',
    line: number,
    crossFrom: number,
    crossTo: number
): AlignmentGuide[] {
    const guides: AlignmentGuide[] = [];
    const seen = new Set<number>();
    for (const ref of refs) {
        const at = axis === 'x' ? ref.x : ref.y;
        if (Math.abs(at - line) > 1e-6 || seen.has(at)) continue;
        seen.add(at);
        const cross = axis === 'x' ? ref.y : ref.x;
        const lo = Math.min(cross, crossFrom, crossTo) - GUIDE_MARGIN;
        const hi = Math.max(cross, crossFrom, crossTo) + GUIDE_MARGIN;
        guides.push(axis === 'x'
            ? { from: [at, lo, ref.z + 0.02], to: [at, hi, ref.z + 0.02] }
            : { from: [lo, at, ref.z + 0.02], to: [hi, at, ref.z + 0.02] });
    }
    return guides;
}
